'use client';

/**
 * ArtifactTable — the table renderer for a `table` artifact.
 *
 * Two variants of ONE component, because a table in the message flow and a table
 * in the viewer want the same typography and different affordances:
 *
 * - `preview` (in the card): the first few rows, read-only, with a footer that
 *   says how much is hidden and offers the expand. No sort/filter controls —
 *   they would be interactive chrome inside a transcript.
 * - `full` (in the viewer / expanded): sortable headers, a filter box, row
 *   numbers, sticky header, and a live "showing N of M" count.
 *
 * Layout rules that matter: the table always scrolls inside its OWN
 * `overflow-x-auto` box (a wide table must never make the page scroll sideways —
 * Z_INDEX_AND_OVERFLOW), the header is sticky in the full variant so column
 * names survive scrolling, and numeric columns are right-aligned with tabular
 * figures because a ragged-left column of money is unreadable.
 */

import React, { useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon, SearchIcon } from 'lucide-react';
import { formatTableCell, type ParsedArtifactTable } from './artifact-utils';
import {
  describeColumns,
  filterTableRows,
  nextSortState,
  sortTableRows,
  type TableSort,
} from './artifact-table-utils';

/** Rows shown in the card before the footer takes over. */
export const TABLE_PREVIEW_ROWS = 8;

export interface ArtifactTableProps {
  table: ParsedArtifactTable;
  variant?: 'preview' | 'full';
  /** Rendered in the preview footer when there is more to see. */
  onExpand?: () => void;
}

function SortIcon({ state }: { state: 'asc' | 'desc' | null }) {
  if (state === 'asc') return <ArrowUpIcon className="size-3" aria-hidden />;
  if (state === 'desc') return <ArrowDownIcon className="size-3" aria-hidden />;
  return <ArrowUpDownIcon className="size-3 opacity-40" aria-hidden />;
}

export function ArtifactTable({ table, variant = 'full', onExpand }: ArtifactTableProps) {
  const [sort, setSort] = useState<TableSort | null>(null);
  const [query, setQuery] = useState('');

  const columns = useMemo(() => describeColumns(table), [table]);

  const rows = useMemo(() => {
    if (variant === 'preview') return table.rows.slice(0, TABLE_PREVIEW_ROWS);
    const filtered = filterTableRows(table.rows, table.columns, query);
    return sortTableRows(filtered, sort, columns);
  }, [table, variant, query, sort, columns]);

  const isFull = variant === 'full';
  const hiddenRows = table.rows.length - rows.length;

  return (
    <div className="space-y-2">
      {isFull && table.rows.length > TABLE_PREVIEW_ROWS && (
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <SearchIcon
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Filter ${table.rows.length} rows…`}
              aria-label="Filter table rows"
              className="w-full min-h-11 rounded-md border border-border bg-transparent pl-8 pr-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {rows.length === table.rows.length
              ? `${table.rows.length} rows`
              : `${rows.length} of ${table.rows.length}`}
          </span>
        </div>
      )}

      {/* The table owns its own horizontal scroll — never the page. */}
      <div
        className={[
          'overflow-x-auto rounded-md border border-border',
          isFull ? 'max-h-[60vh] overflow-y-auto' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid="artifact-table-scroller"
      >
        <table className="w-full border-collapse text-sm [font-variant-numeric:tabular-nums]">
          <thead
            className={[
              'bg-muted/60',
              // Sticky only in the full view: in the flow the card is short
              // enough that a sticky header would just look like a stuck row.
              isFull ? 'sticky top-0 z-10' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <tr>
              {isFull && (
                <th
                  scope="col"
                  className="w-px whitespace-nowrap px-3 py-2 text-right text-xs font-medium text-muted-foreground"
                >
                  #
                </th>
              )}
              {columns.map((column) => {
                const state = sort?.column === column.key ? sort.direction : null;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={
                      state === 'asc' ? 'ascending' : state === 'desc' ? 'descending' : 'none'
                    }
                    className={[
                      'px-3 py-2 font-semibold whitespace-nowrap border-b border-border',
                      column.align === 'right' ? 'text-right' : 'text-left',
                    ].join(' ')}
                  >
                    {isFull ? (
                      <button
                        type="button"
                        onClick={() => setSort((current) => nextSortState(current, column.key))}
                        className={[
                          'inline-flex items-center gap-1.5 hover:text-primary transition-colors',
                          column.align === 'right' ? 'flex-row-reverse' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        aria-label={`Sort by ${column.key}`}
                      >
                        {column.key}
                        <SortIcon state={state} />
                      </button>
                    ) : (
                      column.key
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-t border-border/60 even:bg-muted/20 hover:bg-muted/40 transition-colors"
              >
                {isFull && (
                  <td className="px-3 py-1.5 text-right text-xs text-muted-foreground align-top">
                    {rowIndex + 1}
                  </td>
                )}
                {columns.map((column) => {
                  const text = formatTableCell(row[column.key]);
                  return (
                    <td
                      key={column.key}
                      // `title` gives the full value back after truncation,
                      // which is what makes a narrow column survivable.
                      title={text.length > 40 ? text : undefined}
                      className={[
                        'px-3 py-1.5 align-top',
                        column.align === 'right' ? 'text-right' : 'text-left',
                        isFull ? '' : 'max-w-[22rem] truncate',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {text}
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + (isFull ? 1 : 0)}
                  className="px-3 py-6 text-center text-sm text-muted-foreground"
                >
                  {query ? `No rows match “${query}”.` : 'No rows.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isFull && hiddenRows > 0 && (
        <button
          type="button"
          onClick={onExpand}
          className="w-full min-h-11 rounded-md border border-dashed border-border text-xs font-medium text-primary hover:bg-muted/50 transition-colors"
        >
          Show all {table.rows.length} rows
        </button>
      )}
    </div>
  );
}
